import { mirrorColor } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ColorPalette } from "@/components/color-palette/ColorPalette";
import { InlineNodeField } from "@/components/doc/right-sidebar/active-node-style/fields/InlineNodeField";
import { getGraphCursorContextId } from "@/components/graph/utils";
import { CursorContextProvider } from "@/components/input/CursorContextProvider";
import { Sidebar } from "@/components/sidebar";
import { isNodeEditable } from "@/components/tiptap/lock/utils";
import { twMerge } from "@/utils/twMerge";

import type { GraphExpressionNodeModel } from "@/models/nodes/GraphExpressionNodeModel";
import type { Color } from "@corca/data";

type Props = {
  node: GraphExpressionNodeModel;
  showColorPalette?: boolean;
};

export const GraphExpression = observer(function GraphExpression({
  node,
  showColorPalette,
}: Props) {
  const { doc, inlineNode } = node;
  const t = useTranslations("sidebar.activeNode");
  const editable = isNodeEditable(node);

  if (!inlineNode) {
    return null;
  }

  const handleOnRemove = () => {
    doc.transact(
      () => {
        doc.removeNode(node);
      },
      "GraphExpressions:removeGraphExpressionNode",
      { runInAction: true },
    );
  };

  const handleColorChange = (color: Color) => {
    doc.transact(
      () => {
        node.color = color;
      },
      "GraphExpressions:changeGraphExpressionColor",
      { runInAction: true },
    );
  };

  return (
    <CursorContextProvider
      key={node.id}
      doc={doc}
      id={getGraphCursorContextId(node)}
      boundary={inlineNode}
    >
      <div className="flex items-center [&:not(:last-child)]:mb-2">
        <InlineNodeField
          node={inlineNode}
          className="shadow-block dark:shadow-block-inverted"
          actionClassName="flex items-center"
          aria-label={t("fields.graphExpression")}
          action={
            editable && (
              <Sidebar.Button
                type="button"
                title={t("graph.actions.removeExpression")}
                icon={<div className="w-[10px] h-[1px] bg-document-text rounded-8" />}
                className="w-6 h-6 min-h-6"
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                onClick={handleOnRemove}
              />
            )
          }
        />
        <div className={twMerge(showColorPalette && "flex ml-1.5 min-w-[47px]")}>
          {editable && !inlineNode.isEmpty && (
            <ColorPalette
              currentColor={node.color}
              onSelect={handleColorChange}
              tooltipTitle={t("graph.actions.expressionColor")}
              toggleProps={{
                className: "min-h-[28px]",
                wrapperClassName: "py-0",
              }}
              icon={
                <div className="p-[3px] w-full h-full">
                  <div
                    className={twMerge(
                      "w-full h-full rounded-full",
                      node.color && `bg-${node.color} dark:bg-${mirrorColor(node.color)}`,
                    )}
                  />
                </div>
              }
            />
          )}
        </div>
      </div>
    </CursorContextProvider>
  );
});
